import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, Search, Store } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { money } from "@/lib/signs";
import { useAuth } from "@/context/AuthContext";

/** A classified ad at half size: thumbnail, title, price, who is selling. */
function MiniListing({ l }) {
    const seller = l.store?.name || l.seller_name;
    return (
        <Link to={`/supplies/${l.slug}`} data-testid={`listing-card-${l.slug}`}
            className="group flex items-center gap-3 rounded-xl border border-zinc-200 bg-white p-2 transition-colors hover:border-zinc-400">
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-zinc-100 grayscale">
                {l.images?.[0]
                    ? <img src={mediaUrl(l.images[0])} alt={l.title} loading="lazy" className="h-full w-full object-cover" />
                    : <div className="flex h-full items-center justify-center text-zinc-400"><Search size={16} /></div>}
            </div>
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-zinc-900">{l.title}</p>
                <p className="truncate text-[11px] text-zinc-500">{seller}{l.location ? ` · ${l.location}` : ""}</p>
            </div>
            <p className="shrink-0 font-mono text-sm font-bold text-zinc-900">{l.price ? money(l.price) : "Offer"}</p>
        </Link>
    );
}

/** Other shops' classified ads under our own catalog. Half the size of the catalog — it is the side aisle. */
export function SellerListings({ kind, group, heading, blurb, sellCta = "List yours", limit = 4, testid = "seller-listings" }) {
    const { user } = useAuth();
    const [rows, setRows] = useState(null);

    useEffect(() => {
        api.get("/marketplace/listings", { params: { kind, group } })
            .then(({ data }) => setRows(data.listings.slice(0, limit)))
            .catch(() => setRows([]));
    }, [kind, group, limit]);

    if (rows === null) return null;
    const more = `/supplies${kind ? `?kind=${kind}` : ""}`;

    return (
        <section className="border-t border-zinc-200 bg-zinc-50" data-testid={testid}>
            <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500"><Store size={11} /> Community marketplace</span>
                    <h2 className="font-display text-base font-black tracking-tight text-zinc-900 md:text-lg">{heading}</h2>
                    <p className="hidden text-xs text-zinc-500 md:inline">{blurb}</p>
                    <div className="ml-auto flex items-center gap-3 text-xs font-semibold">
                        <Link to={more} className="inline-flex items-center gap-1 text-zinc-600 hover:text-zinc-900" data-testid={`${testid}-more`}>See all <ArrowRight size={12} /></Link>
                        <Link to={user ? `/supplies/sell?kind=${kind}${group ? `&group=${group}` : ""}` : "/login"} data-testid={`${testid}-sell-cta`}
                            className="inline-flex items-center gap-1 rounded-full border border-zinc-300 px-3 py-1 text-zinc-700 hover:border-zinc-500">
                            <Plus size={12} /> {sellCta}
                        </Link>
                    </div>
                </div>

                {rows.length === 0 ? (
                    <p className="mt-3 rounded-xl border border-dashed border-zinc-300 p-3 text-center text-xs text-zinc-500" data-testid={`${testid}-empty`}>
                        No community listings here yet. Free to post.
                    </p>
                ) : (
                    <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid={`${testid}-grid`}>
                        {rows.map((l) => <MiniListing key={l.slug} l={l} />)}
                    </div>
                )}
            </div>
        </section>
    );
}
